import { StringUtils, Logger } from "neo-common";
import { Scaling } from "./Scaling";
import { Platforms } from "neo-games";
import { BetHigherThanMinDialog } from "../Channels/Common/Dialogs/BetHigherThanMinDialog";

export class RetentionPanel {
    //******************************************************************************************************************
    //  INSTANCE
    //******************************************************************************************************************

    private static instance: RetentionPanel;

    //******************************************************************************************************************
    //  SETTINGS
    //******************************************************************************************************************

    private static panelParentId: string = "root";

    private static initRevalidateTimeout: number = 500;

    private static landscapeMaxIconCount: number = window["CHANNEL"] === "mobile" ? 4 : 5;

    private static portraitMaxIconCount: number = 2;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private static panelWidth: number = 0;

    private static canShow: boolean = false;

    private static peakActive: boolean = false;

    private screen: HTMLElement;

    private content: HTMLElement;

    private panel: HTMLElement;

    private icons: HTMLElement;

    private lastCount: number;

    private iconsSlotsCount: number;

    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    constructor() {
        this.screen = null;
        this.panel = null;
        this.icons = null;
        this.lastCount = 0;

        this.createPanel();
    }

    //******************************************************************************************************************
    //  PUBLIC
    //******************************************************************************************************************

    public revalidate(): void {
        if (!RetentionPanel.canShow) {
            return;
        }

        if (this.screen == null) {
            return;
        }

        if (this.panel == null) {
            return;
        }

        this.log("revalidate");
        if (this.hasActiveIcon()) {
            document.body.classList.add("retention-panel-active");
            RetentionPanel.panelWidth = parseInt(window.getComputedStyle(this.panel).width);
        } else {
            document.body.classList.remove("retention-panel-active");
            RetentionPanel.panelWidth = 0;
        }
        this.showPeakHelpIcon();
        Scaling.LeftPanelWidth = RetentionPanel.panelWidth;
		Scaling.resizeRoot(false, false);
    }

    //******************************************************************************************************************
    //  PRIVATE
    //******************************************************************************************************************

    private showPeakHelpIcon(): void {
        let peakHelp = document.querySelector(".hlpTournamentsBtn");

        if (RetentionPanel.peakActive) {
            let peakHelpIcon = document.querySelector(".btnTournaments");
            let peakHelpIconBackground =
                document.getElementById("peakMenuBtn") != null
                    ? window.getComputedStyle(document.getElementById("peakMenuBtn")).backgroundImage
                    : false;
            let peakHelpIconBackgroundSize =
                document.querySelector(".hlpMenu") != null
                    ? window.getComputedStyle(document.querySelector(".hlpMenu")).width
                    : false;
            if (peakHelp != null && peakHelpIcon != null && peakHelpIconBackground && peakHelpIconBackgroundSize) {
                peakHelpIcon.setAttribute(
                    "style",
                    "background-image:" +
                        peakHelpIconBackground +
                        "; height:100%; background-position: center; background-size: auto " +
                        peakHelpIconBackgroundSize +
                        "; -webkit-transform: scale(0.66); transform: scale(0.66);",
                );
                peakHelp.classList.remove("_hidden");
            } else {
                setTimeout(() => {
                    this.showPeakHelpIcon();
                }, RetentionPanel.initRevalidateTimeout);
            }
        } else {
            peakHelp.classList.add("_hidden");
        }
    }

    private createPanel(): void {
        const root = document.getElementById(RetentionPanel.panelParentId);

        if (root == null) {
            if (DEBUG) {
                console.log(`ERROR - panel with id ${RetentionPanel.panelParentId} was not found`);
            }
            return;
        }

        this.screen = this.createElement("div", "retentionScreen", ["screen"]);
        this.content = this.createElement("div", "retentionContent");
        this.panel = this.createElement("div", "retentionPanel", ["gameControls"]);
        this.icons = this.createElement("div", "retentionPanelIcons");

        this.createContentPlaceholder(this.content);
        this.createIconsPlaceholder(this.icons);

        this.screen.appendChild(this.content);
        this.screen.appendChild(this.panel);
        this.panel.appendChild(this.icons);

        root.appendChild(this.screen);
    }

    private createContentPlaceholder(contentHolder: HTMLElement): void {
        if (contentHolder == null) {
            return;
        }

        contentHolder.appendChild(this.createElement("div", "peakContentHolder", ["retentionContentHolder"]));
        contentHolder.appendChild(this.createElement("div", "frbContentHolder", ["retentionContentHolder"]));
    }

    private createIconsPlaceholder(iconsHolder: HTMLElement): void {
        if (iconsHolder == null) {
            return;
        }

        iconsHolder.appendChild(this.createElement("div", "peakIconHolder", ["retentionIconHolder"]));
        iconsHolder.appendChild(this.createElement("div", "frbIconHolder", ["retentionIconHolder"]));

        for (let i = 0; i < iconsHolder.children.length; i++) {
            let child = iconsHolder.children[i];
            child.addEventListener("click", (event) => {
                BetHigherThanMinDialog.closeDialog();
            });
        }
    }

    private createElement(type: string, id: string = "", classList: Array<string> = []): HTMLElement {
        const elem = document.createElement(type);

        if (!StringUtils.isNullOrEmpty(id)) {
            elem.id = id;
        }

        if (classList.length > 0) {
            for (let i = 0; i < classList.length; i++) {
                elem.classList.add(classList[i]);
            }
        }

        return elem;
    }

    public updateCount(currentCount: number = -1): void {
        currentCount = currentCount == -1 ? this.lastCount : currentCount;

        const isPortrait = Scaling.isPortrait();
        const slots = isPortrait
            ? RetentionPanel.portraitMaxIconCount - currentCount
            : RetentionPanel.landscapeMaxIconCount - currentCount;

        if (this.iconsSlotsCount != slots) {
            this.iconsSlotsCount = slots;
            Platforms.Platforms.iconsCountAvailable(slots);
        }

        const isMultiIcon = this.isMultiIcon(currentCount);
        this.icons.classList.remove(
            "retention-icon-count-" + this.lastCount,
            "retention-icon-count-" + this.lastCount + "-m",
        );
        this.icons.classList.add(
            "retention-icon-count-" + currentCount + (isMultiIcon || RetentionPanel.peakActive ? "-m" : ""),
        );
        this.lastCount = currentCount;
    }

    private isMultiIcon(count: number): boolean {
        if (count == 0 || count > 1) {
            return false;
        }

        const style = window.getComputedStyle(this.icons);
        const width = parseInt(style.width);
        const height = parseInt(style.height);

        return width != 0 ? height / width > 1.5 : false;
    }

    private hasActiveIcon(): boolean {
        if (this.panel == null) {
            return false;
        }

        if (this.icons == null) {
            return false;
        }

        let result = false;
        let count = 0;

        const children = this.icons.children;
        for (let i = 0; i < children.length; i++) {
            const child = children[i] as HTMLElement;
            if (child != null) {
                const iconChildren = child.children;

                if (child.id == "peakIconHolder") {
                    RetentionPanel.peakActive = iconChildren.length > 0;
                }

                for (let j = 0; j < iconChildren.length; j++) {
                    const iconChild = iconChildren[j] as HTMLElement;

                    if (iconChild.getAttribute("active") == "true") {
                        result = true;
                        count++;
                    }
                }
            }
        }

        this.updateCount(count);
        return result;
    }

    protected log(s: string): void {
        if (DEBUG) {
            Logger.debugGroup("RetentionPanel", "RetentionPanel " + s);
        }
    }

    //******************************************************************************************************************
    //  PUBLIC STATIC
    //******************************************************************************************************************

    private static getInstance(): RetentionPanel {
        if (RetentionPanel.instance == null) {
            RetentionPanel.instance = new RetentionPanel();
        }

        return RetentionPanel.instance;
    }

    public static initialize(): void {
        RetentionPanel.getInstance();
    }

    public static show(): void {
        RetentionPanel.canShow = true;
        RetentionPanel.revalidate();

        Scaling.Emitter.on(Scaling.EventResize, () => {
            RetentionPanel.getInstance().updateCount();
            RetentionPanel.getInstance().revalidate();
        });

        //revalidate possible loaded plugins after loading screen is hidden
        setTimeout(() => {
            RetentionPanel.revalidate();
        }, RetentionPanel.initRevalidateTimeout);
    }

    public static revalidate(): void {
        RetentionPanel.getInstance().revalidate();
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Utils/RetentionPanel.ts
 **/